'use client';

import { SearchParams } from '@/constants/listing-search-keys';
import {
  DefaultAdults,
  DefaultChildren,
  DefaultGuests,
  MaxGuests,
  useSearch,
} from '@/context/listing-search-provider';
import {
  parseGuestCountsFromSearchParams,
  totalGuestsFromCounts,
} from '@/utils/guest-search-params';
import clsx, { ClassValue } from 'clsx';

import PopoverDropdown from '@/components/ui/popover-dropdown';
import { SelectStyle } from '@/components/ui/select-input';
import Stepper from '@/components/ui/stepper';

interface GuestSelectionProps {
  name?: string;
  style?: SelectStyle;
  className?: string;
  pos?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left';
  classNames?: Partial<{
    pannel: ClassValue;
    button: ClassValue;
    root: ClassValue;
    footer: ClassValue;
  }>;
  radius?: 'default' | 'square' | 'square-left' | 'square-right';
  required?: boolean;
  hasError?: boolean;
  submitPath?: string;
  shouldSubmit?: boolean;
  onChange?: (value?: number) => void;
  value?: number;
  maxGuests?: number;
  id: string;
}

const GuestSelection: React.FC<GuestSelectionProps> = ({
  name,
  className,
  pos,
  classNames,
  style = 'gray',
  radius = 'default',
  required,
  hasError,
  submitPath,
  shouldSubmit,
  onChange,
  value,
  maxGuests = MaxGuests,
  id,
}) => {
  const { setParam, guests, setGuests, searchParams, applySearchParams } =
    useSearch();
  const guestValue = value || guests || DefaultGuests;
  const urlGuestCounts = parseGuestCountsFromSearchParams(searchParams, {
    adults: DefaultAdults,
    children: DefaultChildren,
  });
  const guestLabel = shouldSubmit
    ? totalGuestsFromCounts(urlGuestCounts).toString()
    : guestValue.toString();

  return (
    <PopoverDropdown
      id={id + '-popover'}
      unmount
      radius={radius}
      style={style}
      value={guestLabel}
      name={name}
      label="Guests"
      onApply={() => {
        if (onChange) {
          onChange(value);
          return;
        }
        shouldSubmit && applySearchParams(submitPath, { scroll: false });
      }}
      onClear={() => {
        if (onChange) {
          onChange(undefined);
          return;
        }
        setParam(SearchParams.GUESTS, undefined, shouldSubmit, submitPath, {
          scroll: false,
        });
      }}
      classNames={{ button: 'w-full', ...classNames }}
      hasError={hasError}
      pos={pos}
      placeholder="Guests"
    >
      <div className={clsx('w-full px-[1.25rem] pt-[0.5rem]', className)}>
        <div className="flex w-full flex-col items-center gap-[0.125rem] pt-[0.5rem]">
          <label className="ut-sm w-full whitespace-nowrap text-center text-gray-900">
            Guests
          </label>
          <div className="pt-0.5">
            <Stepper
              id={id ? `stepper-${id}` : undefined}
              hasError={hasError}
              required={required}
              stepDown={() => {
                if (onChange) {
                  onChange(guestValue - 1);
                  return;
                }
                setGuests(guestValue - 1);
              }}
              stepUp={() => {
                if (maxGuests && guestValue >= maxGuests) {
                  return;
                }
                if (onChange) {
                  onChange(guestValue + 1);
                  return;
                }
                setGuests(guestValue + 1);
              }}
              disableDown={!guests || guests === 1}
              disableUp={!!guests && guests === MaxGuests}
              value={value?.toString() || guests?.toString() || ''}
            />
          </div>
        </div>
      </div>
    </PopoverDropdown>
  );
};

export default GuestSelection;
